import { CONFIG } from '../config';
import { ensureAccessToken } from '../auth';

// A request the network never answers (common on a phone coming back from the background) would otherwise wait for ever; past
// this it is abandoned and retried like any other failure. Well beyond a cold Gateway's slowest first answer (about 5 s).
const ATTEMPT_TIMEOUT_MS = 20_000;

// A Gateway instance that has just started builds the whole platform schema before answering (2 to 5 s), and a burst of
// requests from a page opening starts one instance per request in flight. So when the Gateway has not been used for a while,
// the first request goes alone and the rest wait for it (up to WARM_WAIT_MS): one instance warms up, then serves them all.
const COLD_AFTER_MS = 5 * 60_000;
const WARM_WAIT_MS = 8_000;
let lastAnswerAt = 0;
let warming: Promise<unknown> | null = null;

export async function gql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  if (Date.now() - lastAnswerAt > COLD_AFTER_MS) {
    if (warming) {
      await Promise.race([warming.catch(() => undefined), new Promise(resolve => setTimeout(resolve, WARM_WAIT_MS))]);
    } else {
      const first = send<T>(query, variables);
      warming = first;
      try {
        return await first;
      } finally {
        warming = null;
      }
    }
  }
  return send<T>(query, variables);
}

async function send<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  const token = await ensureAccessToken();
  if (!token) throw new Error('Not authenticated');

  // Stable across every retry attempt below (generated once per call, not per attempt) — lets
  // the gateway's emitEvent resolver recognise a retried mutation whose earlier attempt already
  // succeeded server-side and return that original result instead of creating a duplicate event.
  // Harmless on queries (nothing reads it there).
  const idempotencyKey = crypto.randomUUID();

  let lastError: Error = new Error('Unknown error');
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise(resolve => setTimeout(resolve, 1000));

    let res: Response;
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), ATTEMPT_TIMEOUT_MS);
    try {
      res = await fetch(CONFIG.graphqlUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-Idempotency-Key': idempotencyKey },
        body: JSON.stringify({ query, variables }),
        signal: abort.signal,
      });
    } catch (e) {
      clearTimeout(timer);
      lastError = abort.signal.aborted ? new Error('The server took too long to answer') : e as Error;
      continue;
    }

    if (!res.ok) {
      clearTimeout(timer);
      const body = await res.text().catch(() => '');
      let detail = body;
      try { const j = JSON.parse(body) as { message?: string }; if (j.message) detail = j.message; } catch { /* not JSON */ }
      const err = new Error(`HTTP ${res.status}${detail ? `: ${detail}` : ''}`);
      if (res.status < 500) throw err;
      lastError = err;
      continue;
    }

    const text = await res.text().catch(() => '');
    clearTimeout(timer);
    if (!text) { lastError = new Error(abort.signal.aborted ? 'The server took too long to answer' : 'Empty response from GraphQL gateway'); continue; }
    lastAnswerAt = Date.now();
    let json: { data?: T; errors?: { message: string }[] };
    try { json = JSON.parse(text) as typeof json; } catch { lastError = new Error('Non-JSON response from GraphQL gateway'); continue; }
    if (json.errors?.length) { lastError = new Error(json.errors[0].message); continue; }
    if (json.data === undefined) { lastError = new Error('No data returned from GraphQL gateway'); continue; }
    return json.data;
  }
  throw lastError;
}
